import { supabase } from '@/integrations/supabase/client';
import { Order, OrderStatus } from '@/types/orders';
import { ChatMessage } from '@/types/chat';
import { useAuthStore } from '@/store/authStore';
import { Tables, Enums } from '@/integrations/supabase/types';

// Use database types for the entities
type InventoryItem = Tables<'inventory_items'>;
type Table = Tables<'store_tables'>;

// Notification types for different scenarios
export enum PushNotificationType {
  // Order notifications
  ORDER_PLACED = 'order_placed',
  ORDER_STATUS_UPDATED = 'order_status_updated',
  ORDER_READY = 'order_ready',
  ORDER_COMPLETED = 'order_completed',
  ORDER_CANCELLED = 'order_cancelled',
  
  // Chat notifications
  NEW_MESSAGE = 'new_message',
  MESSAGE_REPLY = 'message_reply',
  
  // Reservation notifications
  RESERVATION_CREATED = 'reservation_created',
  RESERVATION_CONFIRMED = 'reservation_confirmed',
  RESERVATION_CANCELLED = 'reservation_cancelled',
  
  // Table assignment notifications
  TABLE_ASSIGNED = 'table_assigned',
  TABLE_UNASSIGNED = 'table_unassigned',
  
  // Inventory notifications
  INVENTORY_LOW_STOCK = 'inventory_low_stock',
  INVENTORY_OUT_OF_STOCK = 'inventory_out_of_stock',
  INVENTORY_CRITICAL = 'inventory_critical',
}

// Base notification payload interface
interface BaseNotificationPayload {
  type: PushNotificationType;
  title: string;
  body: string;
  data: Record<string, unknown>;
  priority?: 'high' | 'normal' | 'low';
  requireInteraction?: boolean;
  silent?: boolean;
}

// Specific notification payloads
interface OrderNotificationPayload extends BaseNotificationPayload {
  type: PushNotificationType.ORDER_PLACED | PushNotificationType.ORDER_STATUS_UPDATED | PushNotificationType.ORDER_READY | PushNotificationType.ORDER_COMPLETED | PushNotificationType.ORDER_CANCELLED;
  data: {
    orderId: string;
    storeId: string;
    tableId: string;
    tableName: string;
    tableNumber: number;
    status: Enums<'order_status'>;
    totalAmount: number;
    action: string;
    items?: Array<{ name: string; quantity: number }>;
    specialInstructions?: string;
    paymentMethod?: Enums<'payment_method'>;
    tipAmount?: number;
    tipPercentage?: number;
    previousStatus?: Enums<'order_status'>;
    navigationRoute: string;
  };
}

interface ChatNotificationPayload extends BaseNotificationPayload {
  type: PushNotificationType.NEW_MESSAGE | PushNotificationType.MESSAGE_REPLY;
  data: {
    messageId: string;
    senderId: string;
    senderName: string;
    storeId: string;
    messageType: Enums<'message_type'>;
    content: string;
    replyToMessageId?: string;
    navigationRoute: string;
  };
}

interface ReservationNotificationPayload extends BaseNotificationPayload {
  type: PushNotificationType.RESERVATION_CREATED | PushNotificationType.RESERVATION_CONFIRMED | PushNotificationType.RESERVATION_CANCELLED;
  data: {
    reservationId: string;
    storeId: string;
    customerName: string;
    customerEmail: string;
    reservationDate: string;
    reservationTime: string;
    partySize: number;
    tableName?: string;
    specialRequests?: string;
    status: string;
    navigationRoute: string;
  };
}

interface TableAssignmentNotificationPayload extends BaseNotificationPayload {
  type: PushNotificationType.TABLE_ASSIGNED | PushNotificationType.TABLE_UNASSIGNED;
  data: {
    tableId: string;
    tableName: string;
    tableNumber: number;
    storeId: string;
    assignedStaffId?: string;
    assignedStaffName?: string;
    previousStaffId?: string;
    previousStaffName?: string;
    navigationRoute: string;
  };
}

interface InventoryNotificationPayload extends BaseNotificationPayload {
  type: PushNotificationType.INVENTORY_LOW_STOCK | PushNotificationType.INVENTORY_OUT_OF_STOCK | PushNotificationType.INVENTORY_CRITICAL;
  data: {
    inventoryItemId: string;
    itemName: string;
    storeId: string;
    currentQuantity: number;
    minimumQuantity: number;
    unitOfMeasure: string;
    alertType: string;
    navigationRoute: string;
  };
}

type NotificationPayload = 
  | OrderNotificationPayload 
  | ChatNotificationPayload 
  | ReservationNotificationPayload 
  | TableAssignmentNotificationPayload 
  | InventoryNotificationPayload;

class PushNotificationService {
  private currentUser = useAuthStore.getState().user;

  /**
   * Send push notification to specific users
   */
  private async sendToUsers(
    userIds: string[], 
    payload: NotificationPayload
  ): Promise<void> {
    try {
      // Fetch FCM tokens for the users
      const { data: users, error: userError } = await supabase
        .from('users')
        .select('id, fcm_token, push_orders, push_updates, push_marketing')
        .in('id', userIds);

      if (userError) {
        console.error('Error fetching users for notification:', userError);
        return;
      }

      if (!users || users.length === 0) {
        console.log('No users found for notification');
        return;
      }

      // Send notification to each user with valid FCM token
      for (const user of users) {
        if (!user.fcm_token) {
          console.log(`No FCM token for user ${user.id}`);
          continue;
        }

        // Check user's notification settings
        if (!this.shouldSendNotification(user, payload.type)) {
          console.log(`User ${user.id} has disabled ${payload.type} notifications`);
          continue;
        }

        await this.sendSingleNotification(user.fcm_token, payload);
      }
    } catch (error) {
      console.error('Error sending push notifications:', error);
    }
  }

  /**
   * Send push notification to store staff
   */
  private async sendToStoreStaff(
    storeId: string, 
    payload: NotificationPayload,
    excludeUserId?: string
  ): Promise<void> {
    try {
      // Fetch store staff with FCM tokens
      const { data: staff, error: staffError } = await supabase
        .from('store_employees')
        .select(`
          user_id,
          users!inner(
            id,
            fcm_token,
            push_orders,
            push_updates,
            push_marketing
          )
        `)
        .eq('store_id', storeId);

      if (staffError) {
        console.error('Error fetching store staff:', staffError);
        return;
      }

      if (!staff || staff.length === 0) {
        console.log('No staff found for store:', storeId);
        return;
      }

      // Send notification to each staff member
      for (const employee of staff) {
        const user = employee.users;
        
        // Skip if user is excluded (e.g., sender of message)
        if (excludeUserId && user.id === excludeUserId) {
          continue;
        }

        if (!user.fcm_token) {
          console.log(`No FCM token for staff member ${user.id}`);
          continue;
        }

        // Check user's notification settings
        if (!this.shouldSendNotification(user, payload.type)) {
          console.log(`Staff member ${user.id} has disabled ${payload.type} notifications`);
          continue;
        }

        await this.sendSingleNotification(user.fcm_token, payload);
      }
    } catch (error) {
      console.error('Error sending notifications to store staff:', error);
    }
  }

  /**
   * Send a single notification to a specific FCM token
   */
  private async sendSingleNotification(
    fcmToken: string, 
    payload: NotificationPayload
  ): Promise<void> {
    try {
      const notificationData = {
        token: fcmToken,
        title: payload.title,
        body: payload.body,
        data: {
          ...payload.data,
          notificationType: payload.type,
          timestamp: Date.now(),
        },
        priority: payload.priority || 'high',
        requireInteraction: payload.requireInteraction || false,
        silent: payload.silent || false,
      };

      const { error } = await supabase.functions.invoke('fcm-notify', {
        body: notificationData,
      });

      if (error) {
        console.error('Error sending FCM notification:', error);
      } else {
        console.log(`Push notification sent successfully to token: ${fcmToken.substring(0, 20)}...`);
      }
    } catch (error) {
      console.error('Error in sendSingleNotification:', error);
    }
  }

  /**
   * Check if notification should be sent based on user settings
   */
  private shouldSendNotification(
    user: { push_orders?: boolean | null; push_updates?: boolean | null; push_marketing?: boolean | null }, 
    notificationType: PushNotificationType
  ): boolean {
    // Default to true if no settings
    if (user.push_orders === null && user.push_updates === null && user.push_marketing === null) {
      return true;
    }

    switch (notificationType) {
      case PushNotificationType.ORDER_PLACED:
      case PushNotificationType.ORDER_STATUS_UPDATED:
      case PushNotificationType.ORDER_READY:
      case PushNotificationType.ORDER_COMPLETED:
      case PushNotificationType.ORDER_CANCELLED:
        return user.push_orders !== false;
      
      case PushNotificationType.NEW_MESSAGE:
      case PushNotificationType.MESSAGE_REPLY:
        return user.push_updates !== false;
      
      case PushNotificationType.RESERVATION_CREATED:
      case PushNotificationType.RESERVATION_CONFIRMED:
      case PushNotificationType.RESERVATION_CANCELLED:
        return user.push_updates !== false; // Assuming reservation updates are handled by push_updates
      
      case PushNotificationType.TABLE_ASSIGNED:
      case PushNotificationType.TABLE_UNASSIGNED:
        return user.push_updates !== false; // Assuming table updates are handled by push_updates
      
      case PushNotificationType.INVENTORY_LOW_STOCK:
      case PushNotificationType.INVENTORY_OUT_OF_STOCK:
      case PushNotificationType.INVENTORY_CRITICAL:
        return user.push_updates !== false; // Assuming inventory alerts are handled by push_updates
      
      default:
        return true;
    }
  }

  // ===== ORDER NOTIFICATIONS =====

  /**
   * Send notification when order is placed
   */
  async notifyOrderPlaced(order: Order): Promise<void> {
    const payload: OrderNotificationPayload = {
      type: PushNotificationType.ORDER_PLACED,
      title: '🆕 New Order Received!',
      body: `Order #${order.id.slice(0, 8)} from Table ${order.tableNumber} - ${order.tableName}`,
      priority: 'high',
      requireInteraction: true,
      data: {
        orderId: order.id,
        storeId: order.store_id,
        tableId: order.table_id,
        tableName: order.tableName,
        tableNumber: order.tableNumber,
        status: order.status,
        totalAmount: order.totalAmount,
        action: 'created',
        items: order.items?.map(item => ({
          name: item.menu_item_name,
          quantity: item.quantity
        })),
        specialInstructions: order.specialInstructions,
        paymentMethod: order.payment_method,
        tipAmount: order.tip_amount,
        tipPercentage: order.tip_percentage,
        navigationRoute: `/dashboard/orders?orderId=${order.id}`,
      },
    };

    await this.sendToStoreStaff(order.store_id, payload);
  }

  /**
   * Send notification when order status is updated
   */
  async notifyOrderStatusUpdate(
    order: Order, 
    previousStatus: OrderStatus
  ): Promise<void> {
    let notificationType: PushNotificationType;
    let title: string;
    let body: string;
    let requireInteraction = false;

    switch (order.status) {
      case 'preparing':
        notificationType = PushNotificationType.ORDER_STATUS_UPDATED;
        title = '👨‍🍳 Order Being Prepared';
        body = `Order #${order.id.slice(0, 8)} is now being prepared`;
        break;
      
      case 'ready':
        notificationType = PushNotificationType.ORDER_READY;
        title = '✅ Order Ready!';
        body = `Order #${order.id.slice(0, 8)} is ready for pickup`;
        requireInteraction = true;
        break;
      
      case 'completed':
        notificationType = PushNotificationType.ORDER_COMPLETED;
        title = '🎉 Order Completed';
        body = `Order #${order.id.slice(0, 8)} has been completed`;
        break;
      
      case 'cancelled':
        notificationType = PushNotificationType.ORDER_CANCELLED;
        title = '❌ Order Cancelled';
        body = `Order #${order.id.slice(0, 8)} has been cancelled`;
        requireInteraction = true;
        break;
      
      default:
        notificationType = PushNotificationType.ORDER_STATUS_UPDATED;
        title = '📝 Order Updated';
        body = `Order #${order.id.slice(0, 8)} status changed to ${order.status}`;
    }

    const payload: OrderNotificationPayload = {
      type: notificationType,
      title,
      body,
      priority: 'high',
      requireInteraction,
      data: {
        orderId: order.id,
        storeId: order.store_id,
        tableId: order.table_id,
        tableName: order.tableName,
        tableNumber: order.tableNumber,
        status: order.status,
        totalAmount: order.totalAmount,
        action: order.status,
        items: order.items?.map(item => ({
          name: item.menu_item_name,
          quantity: item.quantity
        })),
        specialInstructions: order.specialInstructions,
        paymentMethod: order.payment_method,
        tipAmount: order.tip_amount,
        tipPercentage: order.tip_percentage,
        previousStatus,
        navigationRoute: `/dashboard/orders?orderId=${order.id}`,
      },
    };

    // Send to store staff
    await this.sendToStoreStaff(order.store_id, payload);
  }

  // ===== CHAT NOTIFICATIONS =====

  /**
   * Send notification for new chat message
   */
  async notifyNewMessage(
    message: ChatMessage, 
    storeId: string,
    excludeSenderId?: string
  ): Promise<void> {
    const payload: ChatNotificationPayload = {
      type: PushNotificationType.NEW_MESSAGE,
      title: `💬 New message from ${message.sender_name}`,
      body: message.message_type === 'text' 
        ? message.content.substring(0, 100) + (message.content.length > 100 ? '...' : '')
        : `Sent a ${message.message_type}`,
      priority: 'normal',
      requireInteraction: false,
      data: {
        messageId: message.id,
        senderId: message.sender_id,
        senderName: message.sender_name,
        storeId,
        messageType: message.message_type,
        content: message.content,
        navigationRoute: `/dashboard/chat?messageId=${message.id}`,
      },
    };

    await this.sendToStoreStaff(storeId, payload, excludeSenderId);
  }

  /**
   * Send notification for message reply
   */
  async notifyMessageReply(
    message: ChatMessage,
    replyToMessage: ChatMessage,
    storeId: string,
    excludeSenderId?: string
  ): Promise<void> {
    const payload: ChatNotificationPayload = {
      type: PushNotificationType.MESSAGE_REPLY,
      title: `↩️ Reply from ${message.sender_name}`,
      body: `Replied to: ${replyToMessage.content.substring(0, 50)}...`,
      priority: 'normal',
      requireInteraction: false,
      data: {
        messageId: message.id,
        senderId: message.sender_id,
        senderName: message.sender_name,
        storeId,
        messageType: message.message_type,
        content: message.content,
        replyToMessageId: replyToMessage.id,
        navigationRoute: `/dashboard/chat?messageId=${message.id}&replyTo=${replyToMessage.id}`,
      },
    };

    await this.sendToStoreStaff(storeId, payload, excludeSenderId);
  }

  // ===== RESERVATION NOTIFICATIONS =====

  /**
   * Send notification for new reservation
   */
  async notifyReservationCreated(reservation): Promise<void> {
    const payload: ReservationNotificationPayload = {
      type: PushNotificationType.RESERVATION_CREATED,
      title: '📅 New Reservation',
      body: `${reservation.customer_name} - ${reservation.party_size} people on ${new Date(reservation.reservation_date).toLocaleDateString()}`,
      priority: 'high',
      requireInteraction: true,
      data: {
        reservationId: reservation.id,
        storeId: reservation.store_id,
        customerName: reservation.customer_name,
        customerEmail: reservation.customer_email,
        reservationDate: reservation.reservation_date,
        reservationTime: reservation.reservation_time,
        partySize: reservation.party_size,
        tableName: reservation.table_name || 'Any Table',
        specialRequests: reservation.special_requests,
        status: reservation.status,
        navigationRoute: `/dashboard/reservations?reservationId=${reservation.id}`,
      },
    };

    await this.sendToStoreStaff(reservation.store_id, payload);
  }

  /**
   * Send notification for reservation confirmation
   */
  async notifyReservationConfirmed(reservation): Promise<void> {
    const payload: ReservationNotificationPayload = {
      type: PushNotificationType.RESERVATION_CONFIRMED,
      title: '✅ Reservation Confirmed',
      body: `Reservation for ${reservation.customer_name} has been confirmed`,
      priority: 'normal',
      requireInteraction: false,
      data: {
        reservationId: reservation.id,
        storeId: reservation.store_id,
        customerName: reservation.customer_name,
        customerEmail: reservation.customer_email,
        reservationDate: reservation.reservation_date,
        reservationTime: reservation.reservation_time,
        partySize: reservation.party_size,
        tableName: reservation.table_name || 'Any Table',
        specialRequests: reservation.special_requests,
        status: reservation.status,
        navigationRoute: `/dashboard/reservations?reservationId=${reservation.id}`,
      },
    };

    await this.sendToStoreStaff(reservation.store_id, payload);
  }

  /**
   * Send notification for reservation cancellation
   */
  async notifyReservationCancelled(reservation): Promise<void> {
    const payload: ReservationNotificationPayload = {
      type: PushNotificationType.RESERVATION_CANCELLED,
      title: '❌ Reservation Cancelled',
      body: `Reservation for ${reservation.customer_name} has been cancelled`,
      priority: 'high',
      requireInteraction: true,
      data: {
        reservationId: reservation.id,
        storeId: reservation.store_id,
        customerName: reservation.customer_name,
        customerEmail: reservation.customer_email,
        reservationDate: reservation.reservation_date,
        reservationTime: reservation.reservation_time,
        partySize: reservation.party_size,
        tableName: reservation.table_name || 'Any Table',
        specialRequests: reservation.special_requests,
        status: reservation.status,
        navigationRoute: `/dashboard/reservations?reservationId=${reservation.id}`,
      },
    };

    await this.sendToStoreStaff(reservation.store_id, payload);
  }

  // ===== TABLE ASSIGNMENT NOTIFICATIONS =====

  /**
   * Send notification when table is assigned to staff
   */
  async notifyTableAssigned(
    table: Table,
    assignedStaffId: string,
    assignedStaffName: string,
    previousStaffId?: string,
    previousStaffName?: string
  ): Promise<void> {
    const payload: TableAssignmentNotificationPayload = {
      type: PushNotificationType.TABLE_ASSIGNED,
      title: '🪑 Table Assignment',
      body: `Table ${table.number} - ${table.name} assigned to ${assignedStaffName}`,
      priority: 'normal',
      requireInteraction: false,
      data: {
        tableId: table.id,
        tableName: table.name,
        tableNumber: table.number,
        storeId: table.store_id,
        assignedStaffId,
        assignedStaffName,
        previousStaffId,
        previousStaffName,
        navigationRoute: `/dashboard/tables?tableId=${table.id}`,
      },
    };

    // Send to the newly assigned staff member
    await this.sendToUsers([assignedStaffId], payload);

    // Send to store staff for awareness
    await this.sendToStoreStaff(table.store_id, payload, assignedStaffId);
  }

  /**
   * Send notification when table is unassigned from staff
   */
  async notifyTableUnassigned(
    table: Table,
    previousStaffId: string,
    previousStaffName: string
  ): Promise<void> {
    const payload: TableAssignmentNotificationPayload = {
      type: PushNotificationType.TABLE_UNASSIGNED,
      title: '🔄 Table Unassigned',
      body: `Table ${table.number} - ${table.name} unassigned from ${previousStaffName}`,
      priority: 'normal',
      requireInteraction: false,
      data: {
        tableId: table.id,
        tableName: table.name,
        tableNumber: table.number,
        storeId: table.store_id,
        previousStaffId,
        previousStaffName,
        navigationRoute: `/dashboard/tables?tableId=${table.id}`,
      },
    };

    // Send to the previously assigned staff member
    await this.sendToUsers([previousStaffId], payload);

    // Send to store staff for awareness
    await this.sendToStoreStaff(table.store_id, payload, previousStaffId);
  }

  // ===== INVENTORY NOTIFICATIONS =====

  /**
   * Send notification for inventory alerts
   */
  async notifyInventoryAlert(
    inventoryItem: InventoryItem,
    alertType: 'low_stock' | 'out_of_stock' | 'critical',
    currentQuantity: number,
    minimumQuantity: number
  ): Promise<void> {
    let notificationType: PushNotificationType;
    let title: string;
    let body: string;
    let priority: 'high' | 'normal' | 'low' = 'normal';
    let requireInteraction = false;

    switch (alertType) {
      case 'low_stock':
        notificationType = PushNotificationType.INVENTORY_LOW_STOCK;
        title = '⚠️ Low Stock Alert';
        body = `${inventoryItem.name} is running low (${currentQuantity} ${inventoryItem.unit_of_measure || 'units'} remaining)`;
        break;
      
      case 'out_of_stock':
        notificationType = PushNotificationType.INVENTORY_OUT_OF_STOCK;
        title = '🚨 Out of Stock!';
        body = `${inventoryItem.name} is now out of stock`;
        priority = 'high';
        requireInteraction = true;
        break;
      
      case 'critical':
        notificationType = PushNotificationType.INVENTORY_CRITICAL;
        title = '🔥 Critical Stock Level!';
        body = `${inventoryItem.name} is critically low (${currentQuantity} ${inventoryItem.unit_of_measure || 'units'} remaining)`;
        priority = 'high';
        requireInteraction = true;
        break;
      
      default:
        return;
    }

    const payload: InventoryNotificationPayload = {
      type: notificationType,
      title,
      body,
      priority,
      requireInteraction,
      data: {
        inventoryItemId: inventoryItem.id,
        itemName: inventoryItem.name,
        storeId: inventoryItem.store_id,
        currentQuantity,
        minimumQuantity,
        unitOfMeasure: inventoryItem.unit_of_measure || 'units',
        alertType,
        navigationRoute: `/dashboard/inventory?itemId=${inventoryItem.id}`,
      },
    };

    await this.sendToStoreStaff(inventoryItem.store_id, payload);
  }

  // ===== UTILITY METHODS =====

  /**
   * Send notification to specific users by IDs
   */
  async sendToSpecificUsers(
    userIds: string[], 
    payload: NotificationPayload
  ): Promise<void> {
    await this.sendToUsers(userIds, payload);
  }

  /**
   * Send notification to all store staff
   */
  async sendToAllStoreStaff(
    storeId: string, 
    payload: NotificationPayload,
    excludeUserId?: string
  ): Promise<void> {
    await this.sendToStoreStaff(storeId, payload, excludeUserId);
  }

  /**
   * Send notification to store owner
   */
  async sendToStoreOwner(
    storeId: string, 
    payload: NotificationPayload
  ): Promise<void> {
    try {
      const { data: store, error } = await supabase
        .from('stores')
        .select('owner_id')
        .eq('id', storeId)
        .single();

      if (error || !store?.owner_id) {
        console.error('Error fetching store owner:', error);
        return;
      }

      await this.sendToUsers([store.owner_id], payload);
    } catch (error) {
      console.error('Error sending notification to store owner:', error);
    }
  }
}

// Create singleton instance
export const pushNotificationService = new PushNotificationService(); 